/**
 * MODULE: _shared/frontend-shell/src/components/generative-ui/MessagePreviewCard.tsx
 * PURPOSE: Message preview before sending (messaging + email template confirm flows)
 * DEPENDENCIES: ../../hooks/assistant/types, emailTemplateCreateBridge
 * PUBLIC_API: MessagePreviewCard, mergeEditedFieldsIntoContinuation
 * SIDE_EFFECTS: onAction / onPreviewUpdate / onSaveAsTemplate callbacks only
 * AGENT_SAFETY: 🟢 SAFE
 * RECENT: 2026-07-15 [2.2.1591] — Custom meeting invites show Save as template (Help me ask Jamie…).
 * RECENT_PRIOR: 2026-07-15 [2.2.1589] — schedule_meeting Subject edits → ``title`` in collected_fields (Contoso Meeting Request).
 */

import React, { useCallback, useEffect, useState } from 'react';
import type { MessagePreview } from '../../hooks/assistant/types';
import type { EmailTemplateCreatePrefill } from '@shared/lib/frontend/utils/emailTemplateCreateBridge';
import {
  BookmarkIcon,
  CheckIcon,
  EnvelopeIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

interface MessagePreviewCardProps {
  messagePreview: MessagePreview;
  onAction: (action: string, continuation?: Record<string, unknown>) => void;
  onPreviewUpdate?: (updated: MessagePreview) => void;
  onSaveAsTemplate?: (prefill: EmailTemplateCreatePrefill) => void;
  disabled?: boolean;
}

/** Merge inline subject/body edits into messaging draft or send_email collected_fields. */
export function mergeEditedFieldsIntoContinuation(
  continuation: Record<string, unknown> | undefined,
  subject: string,
  body: string,
  options?: { confirmSend?: boolean }
): Record<string, unknown> {
  const cont = { ...(continuation ?? {}) };
  const draft = cont.draft;
  if (cont.action === 'messaging' && draft && typeof draft === 'object' && !Array.isArray(draft)) {
    cont.draft = { ...(draft as Record<string, unknown>), subject, body };
    return cont;
  }
  const collected = cont.collected_fields;
  if (cont.tool === 'send_email' && collected && typeof collected === 'object' && !Array.isArray(collected)) {
    const cf: Record<string, unknown> = { ...(collected as Record<string, unknown>), subject, body };
    if (options?.confirmSend) {
      cf.template_confirm = true;
    }
    cont.collected_fields = cf;
    return cont;
  }
  // Meeting invite preview: Subject maps to calendar title [2.2.1589].
  if (
    (cont.action === 'schedule_meeting' || cont.tool === 'schedule_meeting') &&
    collected &&
    typeof collected === 'object' &&
    !Array.isArray(collected)
  ) {
    cont.collected_fields = {
      ...(collected as Record<string, unknown>),
      title: subject,
      description: body,
    };
  }
  return cont;
}

export const MessagePreviewCard: React.FC<MessagePreviewCardProps> = ({
  messagePreview,
  onAction,
  onPreviewUpdate,
  onSaveAsTemplate,
  disabled = false,
}) => {
  const isEmailPreview = messagePreview.actions.some(
    (a) => a.action === 'confirm_email_send' || a.action === 'cancel_email_send'
  );
  const isMeetingPreview = messagePreview.actions.some(
    (a) => a.action === 'schedule_meeting'
  );
  const previewTitle = isEmailPreview
    ? 'Email Preview'
    : isMeetingPreview
      ? 'Meeting Preview'
      : 'Message Preview';

  const [isEditing, setIsEditing] = useState(false);
  const [subject, setSubject] = useState(messagePreview.subject);
  const [body, setBody] = useState(messagePreview.body);

  useEffect(() => {
    setSubject(messagePreview.subject);
    setBody(messagePreview.body);
    setIsEditing(false);
  }, [messagePreview.subject, messagePreview.body, messagePreview.recipient_display]);

  const buildUpdatedPreview = useCallback(
    (nextSubject: string, nextBody: string): MessagePreview => ({
      ...messagePreview,
      subject: nextSubject,
      body: nextBody,
      continuation: mergeEditedFieldsIntoContinuation(
        messagePreview.continuation as Record<string, unknown> | undefined,
        nextSubject,
        nextBody
      ) as MessagePreview['continuation'],
    }),
    [messagePreview]
  );

  const handleSave = () => {
    const updated = buildUpdatedPreview(subject, body);
    onPreviewUpdate?.(updated);
    setIsEditing(false);
  };

  const handleSaveAsTemplate = () => {
    onSaveAsTemplate?.({
      name: subject.slice(0, 80) || (isMeetingPreview ? 'Meeting invite' : 'Assistant email draft'),
      subject,
      bodyContent: body,
      category: isMeetingPreview ? 'meeting' : 'follow-up',
    });
  };

  const handleActionClick = (action: string) => {
    const confirmSend =
      action === 'send_message' || action === 'confirm_email_send' || action === 'schedule_meeting';
    const continuation = mergeEditedFieldsIntoContinuation(
      messagePreview.continuation as Record<string, unknown> | undefined,
      subject,
      body,
      { confirmSend }
    );
    onAction(action, continuation);
  };

  const visibleActions = messagePreview.actions.filter((a) => a.action !== 'edit_message');

  const getActionIcon = (action: string) => {
    switch (action) {
      case 'send_message':
      case 'confirm_email_send':
      case 'schedule_meeting':
        return <PaperAirplaneIcon className="w-4 h-4" />;
      case 'cancel':
      case 'cancel_email_send':
        return <XMarkIcon className="w-4 h-4" />;
      default:
        return null;
    }
  };

  const getButtonStyle = (style: string) => {
    switch (style) {
      case 'primary':
        return 'bg-blue-600 text-white hover:bg-blue-700';
      case 'destructive':
        return 'bg-red-600 text-white hover:bg-red-700';
      case 'secondary':
      default:
        return 'border border-gray-300 text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700';
    }
  };

  return (
    <div className="mt-2 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex items-center gap-2">
          <EnvelopeIcon className="w-5 h-5 text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{previewTitle}</h3>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{messagePreview.message}</p>
      </div>

      {/* Message Content */}
      <div className="p-4 space-y-3">
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            To
          </label>
          <p className="text-sm text-gray-900 dark:text-white mt-1">{messagePreview.recipient_display}</p>
        </div>
        <div>
          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
            Subject
          </label>
          {isEditing ? (
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={disabled}
              className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-2.5 py-1.5 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Email subject"
              data-testid="assistant-message-preview-subject-input"
            />
          ) : (
            <p className="text-sm font-semibold text-gray-900 dark:text-white mt-1">{subject}</p>
          )}
        </div>
        <div>
          <div className="flex items-center justify-between gap-2">
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              Message
            </label>
            <div className="flex items-center gap-1">
              {isEditing ? (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={handleSave}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-primary-700 transition hover:bg-primary-50 disabled:opacity-50 dark:text-primary-300 dark:hover:bg-primary-950/40"
                  data-testid="assistant-message-preview-save"
                >
                  <CheckIcon className="h-3.5 w-3.5" aria-hidden />
                  Save
                </button>
              ) : (
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => setIsEditing(true)}
                  className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-gray-500 transition hover:bg-gray-100 hover:text-primary-600 disabled:opacity-50 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-primary-400"
                  aria-label="Edit message"
                  data-testid="assistant-message-preview-edit"
                >
                  <PencilSquareIcon className="h-3.5 w-3.5" aria-hidden />
                  Edit
                </button>
              )}
            </div>
          </div>
          {isEditing ? (
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={disabled}
              rows={8}
              className="mt-1 w-full resize-y rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm leading-relaxed text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
              aria-label="Email body"
              data-testid="assistant-message-preview-body-input"
            />
          ) : (
            <div className="mt-1 bg-gray-50 dark:bg-gray-800 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
              <p className="text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">{body}</p>
            </div>
          )}
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 pb-4 flex flex-wrap gap-2">
        {visibleActions.map((action, idx) => (
          <button
            key={idx}
            type="button"
            disabled={disabled || isEditing}
            onClick={() => handleActionClick(action.action)}
            data-testid={
              action.action === 'send_message' ||
              action.action === 'confirm_email_send' ||
              action.action === 'schedule_meeting'
                ? 'assistant-message-preview-send'
                : action.action === 'cancel' || action.action === 'cancel_email_send'
                  ? 'assistant-message-preview-cancel'
                  : undefined
            }
            className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 ${
              idx === 0 ? 'flex-1' : ''
            } ${getButtonStyle(action.style)}`}
          >
            {getActionIcon(action.action)}
            {action.label}
          </button>
        ))}
        {(isEmailPreview || (isMeetingPreview && messagePreview.show_save_as_template)) &&
        onSaveAsTemplate ? (
          <button
            type="button"
            disabled={disabled || isEditing}
            onClick={handleSaveAsTemplate}
            className="inline-flex items-center justify-center gap-2 rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            data-testid="assistant-message-preview-save-template"
          >
            <BookmarkIcon className="h-4 w-4" aria-hidden />
            Save as template
          </button>
        ) : null}
      </div>
    </div>
  );
};
